import React, { useState, useEffect, useRef } from 'react';
import Navbar from '../../components/Navbar/Navbar';
import Footer from '../../components/Footer/Footer';
import {
  UserCheck, UploadCloud, Truck, Wallet, Mail, FileText,
  Building, CheckCircle, ExternalLink, Play, Sparkles, ShoppingBag
} from 'lucide-react';
import './HowItWorksPage.css';

// Defined outside component — stable reference, no stale-closure risk.
const HOW_SIDEBAR_ITEMS = [
  { label: 'Register Your Account', id: 'register-account' },
  { label: 'Listing Your Product', id: 'listing-product' },
  { label: 'What Is The Haatza Seller Panel?', id: 'seller-panel' },
  { label: 'What Is A Catalog?', id: 'what-is-catalog' },
  { label: 'How To Upload A Product?', id: 'upload-product' },
  { label: 'Getting Orders', id: 'getting-orders' },
  { label: 'Delivery & Payment', id: 'delivery-payment' }
];

export default function HowItWorksPage() {
  const [activeSection, setActiveSection] = useState('register-account');
  const sectionsRef = useRef({});
  const sidebarItems = HOW_SIDEBAR_ITEMS;

  const [sidebarState, setSidebarState] = useState('top'); // 'top' | 'fixed' | 'docked'
  const mainLayoutRef = useRef(null);
  const sidebarNavRef = useRef(null);
  // Functional Sidebar ScrollSpy using IntersectionObserver
  useEffect(() => {
    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observerOptions = {
      root: null,
      rootMargin: '-90px 0px -50% 0px',
      threshold: 0
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    HOW_SIDEBAR_ITEMS.forEach((item) => {
      const el = document.getElementById(item.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  // Three-phase sticky: 'top' (in flow) -> 'fixed' (pinned below navbar)
  // -> 'docked' (absolutely positioned at bottom of the column so it
  // never overlaps the footer). This mirrors GrowPage's sidebar and
  // avoids relying on CSS position:sticky.
  useEffect(() => {
    const TOP_OFFSET = 118;
    let sidebarHeight = 0;

    const checkSidebarState = () => {
      const container = mainLayoutRef.current;
      if (!container) return;
      if (!sidebarHeight) {
        sidebarHeight = container.querySelector('.sidebar-inner')?.offsetHeight || 0;
      }
      const rect = container.getBoundingClientRect();

      const navEl = sidebarNavRef.current;
      if (navEl) {
        const navRect = navEl.getBoundingClientRect();
        container.style.setProperty('--sidebar-fixed-left', `${navRect.left}px`);
      }

      let nextState;
      if (rect.top > TOP_OFFSET) {
        nextState = 'top';
      } else if (rect.bottom < TOP_OFFSET + sidebarHeight) {
        nextState = 'docked';
      } else {
        nextState = 'fixed';
      }

      setSidebarState((prev) => (prev === nextState ? prev : nextState));
    };

    checkSidebarState();
    window.addEventListener('scroll', checkSidebarState, { passive: true });
    window.addEventListener('resize', checkSidebarState, { passive: true });
    return () => {
      window.removeEventListener('scroll', checkSidebarState);
      window.removeEventListener('resize', checkSidebarState);
    };
  }, []);

  const handleSidebarClick = (id) => {
    setActiveSection(id);
    const targetElement = document.getElementById(id);
    if (targetElement) {
      const offset = 90;
      const elementPosition = targetElement.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="how-it-works-wrapper">
      <Navbar />

      {/* SECTION 1: HERO */}
      <section className="how-hero-section" id="how-top">
        <div className="container hero-grid-2">
          <div className="hero-copy">
            <h1 className="hero-heading">
              How to Sell on <span className="highlight-blue">Haatza</span>
            </h1>
            <p className="hero-subtext">
              Become a Haatza seller and join <span className="highlight-blue">5 lakh+ sellers</span> who are growing their business everyday
            </p>
            <div className="hero-callout-simple">
              <span className="new-badge-pill">NEW</span>
              <div className="gst-callout-text">
                <div className="gst-callout-line">Don't have a GSTIN or have a Composition GSTIN?</div>
                <div className="gst-callout-line">You can still sell on Haatza. Click <a href="/donothavegst" className="highlight-blue underline font-semibold">here</a> to know more.</div>
              </div>
            </div>
            <a href="/signin" style={{ textDecoration: 'none' }}>
              <button className="hero-cta-btn">Start Selling</button>
            </a>
          </div>

          <div className="hero-visual-full">
            <img
              src="https://static.wixstatic.com/media/fe689e_c5710531333e4400a0abc5dd50d1f586~mv2.png/v1/fill/w_848,h_534,fp_0.60_0.22,q_90,usm_0.66_1.00_0.01,enc_avif,quality_auto/home_edited.png"
              alt="Haatza Stockroom Seller"
              className="hero-full-edge-img"
            />
          </div>
        </div>
      </section>

      {/* SECTION 2: BECOME A SELLER IN SIMPLE STEPS */}
      <section className="simple-steps-section">
        <div className="container steps-container-wide">
          <h2 className="section-h2 text-center how-steps-heading">Become a seller on Haatza in simple steps</h2>

          <div className="steps-row-4">
            <div className="simple-step-card">
              <div className="step-card-header">
                <div className="step-badge-icon"><img src="https://static.wixstatic.com/media/fe689e_326c52ff72d048e0928ebb1f52beb981~mv2.jpg/v1/fill/w_72,h_66,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-26%20at%203_48_edited.jpg" alt="" className="step-badge-img" /></div>
                <h4 className="simple-step-title">Sign up for free</h4>
              </div>
              <p className="simple-step-desc">
                Register as a Haatza Seller. All you need is an active bank account and your GSTIN number (for GST sellers) or Enrolment ID / UIN (for non-GST sellers).
              </p>
            </div>

            {/* Step 2 */}
            <div className="simple-step-card">
              <div className="step-card-header">
                <div className="step-badge-icon"><img src="https://static.wixstatic.com/media/fe689e_1d65cef4eec546d49b3582f472449295~mv2.jpg/v1/fill/w_51,h_66,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-26%20at%203_48_edited.jpg" alt="" className="step-badge-img" /></div>
                <h4 className="simple-step-title">Upload your product</h4>
              </div>
              <p className="simple-step-desc">
                After completing the registration, upload your product catalog on the Haatza Seller Panel.
              </p>
            </div>

            {/* Step 3 */}
            <div className="simple-step-card">
              <div className="step-card-header">
                <div className="step-badge-icon"><img src="https://static.wixstatic.com/media/fe689e_69a1d78276f64fb4935fb057d6b93b03~mv2.jpg/v1/fill/w_67,h_66,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-26%20at%203_49_edited.jpg" alt="" className="step-badge-img" /></div>
                <h4 className="simple-step-title">Receive & Ship Orders</h4>
              </div>
              <p className="simple-step-desc">
                Haatza charges the lowest shipping cost for deliveries across India (for GST sellers) and within the state (for non-GST sellers).
              </p>
            </div>

            {/* Step 4 */}
            <div className="simple-step-card">
              <div className="step-card-header">
                <div className="step-badge-icon"><img src="https://static.wixstatic.com/media/fe689e_c28748a6e0024e71bc2993550d79aa54~mv2.jpg/v1/fill/w_67,h_66,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-26%20at%203_50_edited.jpg" alt="" className="step-badge-img" /></div>
                <h4 className="simple-step-title">Receive Payments</h4>
              </div>
              <p className="simple-step-desc">
                Payment is securely deposited directly to your bank account on Haatza following a 7-day payment cycle from order delivery, including Cash on Delivery orders.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3: SCROLL-SPY LAYOUT (Sidebar + Content) */}
      <div className="how-main-layout" ref={mainLayoutRef}>

        {/* Sticky Left Sidebar */}
        <aside
          ref={sidebarNavRef}
          className={`how-sidebar-nav ${sidebarState === 'fixed' ? 'how-sidebar-nav--fixed' : ''} ${sidebarState === 'docked' ? 'how-sidebar-nav--docked' : ''}`}
        >
          <div className="sidebar-inner">
            <ul className="sidebar-links">
              {sidebarItems.map((item) => (
                <li key={item.id}>
                  <button
                    onClick={() => handleSidebarClick(item.id)}
                    className={`sidebar-link-btn ${activeSection === item.id ? 'active' : ''}`}
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* Right Scrollable Content */}
        <main className="how-content-container">

          {/* SECTION 4: REGISTER YOUR ACCOUNT */}
          <section
            className="how-content-section section-anchor"
            id="register-account"
            ref={(el) => (sectionsRef.current['register-account'] = el)}
          >
            <div className="how-page-specific-heading-wrapper">
              <span className="how-page-specific-heading-text">Register Your Account</span>
            </div>
            <p className="section-intro-text">
              To start selling on Haatza, you need to first create an account on Haatza. All you need is:
            </p>

            <div className="req-cards-grid-3">
              <div className="req-card">
                <div className="req-icon"><img src="https://static.wixstatic.com/media/fe689e_95aad345b1974df184b72c160c176a36~mv2.png/v1/fill/w_68,h_66,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-25%20at%2011_edited.png" alt="" className="req-icon-img" /></div>
                <div className="req-card-text">
                  <h4 className="req-card-title">Email ID</h4>
                </div>
              </div>
              <div className="req-card">
                <div className="req-icon"><img src="https://static.wixstatic.com/media/fe689e_f9c05074dd424ea98677e85ccffdad09~mv2.jpg/v1/fill/w_67,h_66,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-08%20at%209_46_edited.jpg" alt="" className="req-icon-img" /></div>
                <div className="req-card-text">
                  <h4 className="req-card-title">GSTIN Number (for GST sellers) or Enrolment ID / UIN (for non-GST sellers)</h4>
                </div>
              </div>
              <div className="req-card">
                <div className="req-icon"><img src="https://static.wixstatic.com/media/fe689e_8760f38f7d1043bd86f1f35c5cd45193~mv2.jpg/v1/fill/w_64,h_66,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-23%20at%209_55_edited.jpg" alt="" className="req-icon-img" /></div>
                <div className="req-card-text">
                  <h4 className="req-card-title">Active Bank Account with the same GST</h4>
                </div>
              </div>
            </div>

            <p className="closing-note-text mt-4 text-center">
              Once you have provided these details, enter your Store Name which will be the name of your online business. Add the Pickup Address from which orders will be picked up by our logistics partners. And, that's all. Your free account on Haatza will be created.
            </p>
          </section>

          {/* SECTION 5: LISTING YOUR PRODUCT */}
          <section
            className="how-content-section section-anchor"
            id="listing-product"
            ref={(el) => (sectionsRef.current['listing-product'] = el)}
          >
            <div className="how-page-specific-heading-wrapper">
              <span className="how-page-specific-heading-text">Listing Your Product</span>
            </div>
            <p className="section-intro-text">
              On completing the registration, you will have access to the Haatza Supplier Panel.
            </p>
          </section>

          {/* SECTION 6: WHAT IS THE HAATZA SELLER PANEL */}
          <section
            className="how-content-section section-anchor"
            id="seller-panel"
            ref={(el) => (sectionsRef.current['seller-panel'] = el)}
          >
            <div className="how-page-specific-heading-wrapper">
              <span className="how-page-specific-heading-text">What Is The Haatza Seller Panel?</span>
            </div>
            <p className="section-intro-text mb-3">
              Haatza Supplier Panel is the one-stop solution for all your online selling needs. From listing your products and managing inventory to processing orders and tracking payments, log in to the Supplier panel and manage your online business with ease. The process is simple, and to know more you can watch the below video.
            </p>
            <p className="section-intro-text mb-3">
              We recommend you review the legal and policies section in the Supplier Panel to ensure your uploaded catalogues are in compliance with the policies of Haatza.
            </p>
            <p className="section-intro-text mb-4">
              You will need to upload your catalog to start selling on Haatza. You will be able to upload your product catalog by logging on to the Haatza Supplier Panel.
            </p>

            <div className="youtube-player-wrapper">
              <iframe
                src="https://www.youtube.com/embed/a6tEKsom-dA"
                title="Sell Smarter, Earn Faster | Haatza Seller"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="embed-iframe"
              ></iframe>
            </div>
          </section>

          {/* SECTION 7: WHAT IS A CATALOG */}
          <section
            className="how-content-section section-anchor"
            id="what-is-catalog"
            ref={(el) => (sectionsRef.current['what-is-catalog'] = el)}
          >
            <div className="how-page-specific-heading-wrapper">
              <span className="how-smaller-heading-text">What Is A Catalog?</span>
            </div>
            <p className="section-intro-text mb-3">
              A catalog is basically the list of products that you want to sell. You can upload a single catalog or you can upload catalogs in bulk using a csv (excel) file. Within each catalog, you can have a minimum of 1 product and a maximum of 9 products of the same category that you want to sell.
            </p>
            <p className="section-intro-text">
              For example, you want to sell 6 sarees, then within the same catalog, you can upload all the 6 products together. But if you want to sell 1 saree and 1 kurta, you will have to upload a different catalog for each category.
            </p>
          </section>

          {/* SECTION 8: HOW TO UPLOAD A PRODUCT */}
          <section
            className="how-content-section section-anchor"
            id="upload-product"
            ref={(el) => (sectionsRef.current['upload-product'] = el)}
          >
            <div className="how-page-specific-heading-wrapper">
              <span className="how-smaller-heading-text">How To Upload A Catalog?</span>
            </div>
            <p className="section-intro-text mb-3">
              It is now very easy to upload single catalogs or bulk catalogs on Haatza! The product catalog gets live post 72 hours from the time of upload.
            </p>
            <p className="section-intro-text mb-3">
              While uploading a catalog, you will have to select a category first and then upload the product images. Uploading more than one image for your product is recommended to give the customers a better view of your product. The detailed image guidelines will be provided in the Haatza Supplier panel. For each product, you will have to then fill in details like the Price, GST, etc.
            </p>
            <p className="section-intro-text mb-3">
              It is recommended that you create a catalog with at least 3-4 products to increase the chances of getting an order. We also recommend uploading at least 5-7 catalogs in the first few days to get better visibility.
            </p>
            <p className="section-intro-text mb-4">
              We recommend you review the legal and policies section in the Supplier Panel to ensure your uploaded catalogues are in compliance with the policies of Haatza.
            </p>

            <div className="how-upload-card-box">
              <div className="how-smaller-callout-heading">
                Learn How To Upload A Product in Haatza
              </div>
            </div>

            <div className="youtube-player-wrapper">
              <iframe
                src="https://www.youtube.com/embed/XZKYAJTId6U"
                title="How to List Products on Haatza"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="embed-iframe"
              ></iframe>
            </div>
          </section>

          {/* SECTION 9: GETTING ORDERS */}
          <section
            className="how-content-section section-anchor"
            id="getting-orders"
            ref={(el) => (sectionsRef.current['getting-orders'] = el)}
          >
            <div className="how-page-specific-heading-wrapper">
              <span className="how-page-specific-heading-text">Getting Orders</span>
            </div>
            <p className="section-intro-text mb-4">
              Once your catalog becomes live, you can start selling on Haatza. More than 11 crore active customers across India will be able to view and purchase your products. To increase your sales and attract more customers, you can:
            </p>

            <div className="orders-tips-list">
              <div className="tip-stacked-card">
                <h4 className="tip-title">List more Product</h4>
                <p className="tip-desc">
                  It usually takes 5-7 catalogs to start getting orders on Haatza. The more catalogs you upload, the better are your chances of getting orders.
                </p>
              </div>

              <div className="tip-stacked-card">
                <h4 className="tip-title">Set the Right Price</h4>
                <p className="tip-desc">
                  To set the right price of the products you want to sell on Haatza, consider the associated costs and margin. Set a competitive price for the products so that the customers find them more appealing than the competitive brands.
                </p>
              </div>

              <div className="tip-stacked-card">
                <h4 className="tip-title">Use Price Recommendation Tool</h4>
                <p className="tip-desc">
                  You can use the price recommendation tool to arrive at a competitive price and increase your sales and visibility by getting an edge over other sellers.
                </p>
              </div>

              <div className="tip-stacked-card">
                <h4 className="tip-title">Right Trend</h4>
                <p className="tip-desc">
                  Customers always prefer fresh and unique trends. So, try to list trending products to get more orders.
                </p>
              </div>

              <div className="tip-stacked-card">
                <h4 className="tip-title">Opt for Next Day Dispatch (NDD) Program</h4>
                <p className="tip-desc">
                  Sign up for Haatza's NDD program to increase your sales. Haatza provides extra visibility to all catalogs that qualify for the NDD program.
                </p>
              </div>
            </div>
          </section>

          {/* SECTION 10: DELIVERY & PAYMENT */}
          <section
            className="how-content-section section-anchor"
            id="delivery-payment"
            ref={(el) => (sectionsRef.current['delivery-payment'] = el)}
          >
            <div className="how-page-specific-heading-wrapper">
              <span className="how-page-specific-heading-text">Delivery & Payment</span>
            </div>
            <p className="section-intro-text mb-3">
              When you receive an order for your product, you are notified via email. You can also check the order update on the Haatza Supplier panel.
            </p>
            <p className="section-intro-text mb-4">
              Haatza charges you the lowest shipping cost for deliveries across India. Our logistics partner picks up the product from your location and delivers it straight to the customer.
            </p>

            <div className="fulfill-callout-card">
              To fulfill an order, go to the Haatza Supplier Panel and follow these steps
            </div>
            <div className="fulfill-steps-grid-4 mt-4">
              <div className="fulfill-step-pill">
                <strong>Accept your order</strong>
              </div>
              <div className="fulfill-step-pill">
                <strong>Download label and manifest</strong>
              </div>
              <div className="fulfill-step-pill">
                <strong>Pack the product and paste the label</strong>
              </div>
              <div className="fulfill-step-pill">
                <strong>Hand over the product to Haatza's logistics partners</strong>
              </div>
            </div>

            <div className="payment-cards-grid-2 mt-6">
              <div className="payment-info-card">
                <h4 className="payment-card-title">Opt for Next Day Dispatch (NDD) Program</h4>
                <p className="payment-card-desc">
                  Haatza charges a 0% commission rate from suppliers across all product categories. With no seller commission, Haatza is the most profitable platform for you to sell products online.
                </p>
              </div>

              <div className="payment-info-card">
                <h4 className="payment-card-title">Payment for Orders</h4>
                <p className="payment-card-desc mb-3">
                  The payment for your orders is securely deposited directly in your bank account on the 7th day from order delivery, including Cash on Delivery orders. (If your product is delivered on 1st January 2025, the payment for that order will be deposited in your bank account on 8th January 2025).
                </p>
                <p className="payment-card-desc">
                  You can view your deposited balance and future payments on the Haatza Supplier Panel.
                </p>
              </div>
            </div>
          </section>

        </main>
      </div>

      {/* SECTION 11: SELLER SUPPORT CENTER (Full-width) */}
      <section
        className="support-center-fullwidth-section light-blue-bg section-anchor"
        id="support-center"
        ref={(el) => (sectionsRef.current['support-center'] = el)}
      >
        <div className="support-center-inner-container">
          <div className="support-badge-brand">
            <span className="brand-h">Haatza</span>
            <span className="brand-divider-pipe"></span>
            <div className="brand-sub-stacked">
              <span className="brand-sub-top">Seller</span>
              <span className="brand-sub-bottom">
                <span className="brand-support-text">Support</span> <span className="brand-center-text">Center</span>
              </span>
            </div>
          </div>

          <div className="support-grid-2">
            <div className="support-copy">
              <h2 className="section-h2 support-2row-heading">
                Haatza Supplier Support<br />Available 24/7
              </h2>
            </div>

            <div className="support-action-box">
              <p className="support-box-text">
                Haatza supplier support is available to solve all your doubts and issues before and after you start your online selling business.
              </p>
              <div className="email-link-with-icon">
                <img
                  src="https://static.wixstatic.com/media/fe689e_95aad345b1974df184b72c160c176a36~mv2.png/v1/fill/w_76,h_75,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-25%20at%2011_edited.png"
                  alt="Support Mail Icon"
                  className="support-mail-img"
                />
                <p className="contact-email-link">
                  You can reach out to<br />
                  <a href="mailto:sales@haatza.com">sales@haatza.com</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
